"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBlockNumber = exports.useBlock = void 0;
const react_query_1 = require("@tanstack/react-query");
const providers_1 = require("../providers");
/**
 * Hook for fetching a block.
 *
 * @remarks
 *
 * Specify which block to fetch with the `blockIdentifier` argument.
 * Control if and how often data is refreshed with `refetchInterval`.
 *
 * @example
 * This example shows how to fetch the latest block only once.
 * ```tsx
 * function Component() {
 *   const { data, isLoading, isError } = useBlock({
 *     refetchInterval: false,
 *     blockIdentifier: 'latest'
 *   })
 *
 *   if (isLoading) return <span>Loading...</span>
 *   if (isError) return <span>Error...</span>
 *   return <span>Hash: {data.block_hash}</span>
 * }
 * ```
 *
 * @example
 * This example shows how to fetch the pending block every 3 seconds.
 * Use your browser network monitor to verify that the hook is refetching the
 * data.
 * ```tsx
 * function Component() {
 *   const { data, isLoading, isError } = useBlock({
 *     refetchInterval: 3000,
 *     blockIdentifier: 'pending'
 *   })
 *
 *   if (isLoading) return <span>Loading...</span>
 *   if (isError) return <span>Error...</span>
 *   return <span>Hash: {data.block_hash}</span>
 * }
 * ```
 */
function useBlock({ refetchInterval, onSuccess, blockIdentifier = 'latest', } = {}) {
    const { library } = (0, providers_1.useStarknet)();
    const { data, error, isStale: isIdle, isLoading, isFetching, isSuccess, isError, isFetched, isFetchedAfterMount, isRefetching, refetch, status, } = (0, react_query_1.useQuery)(['block', blockIdentifier], fetchBlock({ library, args: { blockIdentifier } }), {
        refetchInterval,
        useErrorBoundary: true,
        onSuccess: (block) => {
            if (block && onSuccess)
                onSuccess(block);
        },
    });
    return {
        data,
        error,
        isIdle,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        isFetched,
        isFetchedAfterMount,
        isRefetching,
        refetch,
        status,
    };
}
exports.useBlock = useBlock;
function fetchBlock({ library, args, }) {
    return async () => {
        return await library.getBlock(args.blockIdentifier);
    };
}
/**
 * Hook for fetching the current block number.
 *
 * @remarks
 *
 * Control if and how often data is refreshed with `refetchInterval`.
 *
 * @example
 * This example shows how to fetch the current block only once.
 * ```tsx
 * function Component() {
 *   const { data, isLoading, isError } = useBlockNumber({
 *     refetchInterval: false
 *   })
 *
 *   if (isLoading) return <span>Loading...</span>
 *   if (isError) return <span>Error...</span>
 *   return <span>Block number: {data}</span>
 * }
 * ```
 *
 * @example
 * This example shows how to fetch the current block every 3 seconds.
 * Use your browser network monitor to verify that the hook is refetching the
 * data.
 * ```tsx
 * function Component() {
 *   const { data, isLoading, isError } = useBlockNumber({
 *     refetchInterval: 3000
 *   })
 *
 *   if (isLoading) return <span>Loading...</span>
 *   if (isError) return <span>Error...</span>
 *   return <span>Block Number: {data}</span>
 * }
 * ```
 */
function useBlockNumber({ refetchInterval, onSuccess, } = {}) {
    const { library } = (0, providers_1.useStarknet)();
    const { data, error, isStale: isIdle, isLoading, isFetching, isSuccess, isError, isFetched, isFetchedAfterMount, isRefetching, refetch, status, } = (0, react_query_1.useQuery)(['block', 'latest'], fetchBlock({ library, args: { blockIdentifier: 'latest' } }), {
        refetchInterval,
        useErrorBoundary: true,
        onSuccess: (block) => {
            if (block && onSuccess)
                onSuccess(block.block_number);
        },
    });
    return {
        data: data?.block_number,
        error,
        isIdle,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        isFetched,
        isFetchedAfterMount,
        isRefetching,
        refetch,
        status,
    };
}
exports.useBlockNumber = useBlockNumber;
//# sourceMappingURL=block.js.map